/**
 * IssueSeal — circular editorial seal stamp with text on a curved path.
 * Mirrors the "ISSUE Nº 01 / SPRING 2026" stamp from the after-mockups.
 *
 * @example
 *   <IssueSeal issue="01" season="Spring" year="2026" />
 *   <IssueSeal issue="02" season="Autumn" year="2026" size={120} tone="cream" />
 */
import React, { useId } from "react";

const TONE = {
  gold:     { stroke: "var(--gold-base)", text: "var(--gold-base)" },
  cream:    { stroke: "var(--cream)",     text: "var(--cream)" },
  charcoal: { stroke: "var(--charcoal)",  text: "var(--charcoal)" },
};

export default function IssueSeal({
  issue = "01",
  season = "Spring",
  year = "2026",
  size = 144,
  tone = "gold",
  className = "",
  ...rest
}) {
  const id = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const T = TONE[tone] || TONE.gold;
  const r = size / 2;
  const inner = r - 10;
  const textR = r - 16;
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label={`Issue number ${issue}, ${season} ${year}`}
      className={className}
      {...rest}
    >
      <defs>
        <path id={`seal-top-${id}`} d={`M ${r - textR} ${r} a ${textR} ${textR} 0 0 1 ${textR * 2} 0`} fill="none" />
        <path id={`seal-bot-${id}`} d={`M ${r - textR} ${r} a ${textR} ${textR} 0 0 0 ${textR * 2} 0`} fill="none" />
      </defs>
      <circle cx={r} cy={r} r={r - 1} fill="none" stroke={T.stroke} strokeWidth="1" />
      <circle cx={r} cy={r} r={inner} fill="none" stroke={T.stroke} strokeWidth="0.5" opacity="0.6" />
      <text fill={T.text} style={{ fontFamily: "Manrope, system-ui, sans-serif", fontSize: 9, letterSpacing: "0.22em", textTransform: "uppercase" }}>
        <textPath href={`#seal-top-${id}`} startOffset="50%" textAnchor="middle">
          {`Issue Nº ${issue}`.toUpperCase()}
        </textPath>
      </text>
      <text fill={T.text} style={{ fontFamily: "Manrope, system-ui, sans-serif", fontSize: 9, letterSpacing: "0.22em", textTransform: "uppercase" }}>
        <textPath href={`#seal-bot-${id}`} startOffset="50%" textAnchor="middle">
          {`${season} ${year}`.toUpperCase()}
        </textPath>
      </text>
      <text x={r} y={r + 5} textAnchor="middle" fill={T.text}
        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontStyle: "italic", fontSize: size * 0.22 }}>
        {issue}
      </text>
    </svg>
  );
}
